import { useCallback, useEffect, useRef, useState } from "react";
import {
  WS_PATH,
  type ClientMessage,
  type GameDto,
  type ProposedMove,
  type ServerMessage,
} from "@chess/shared";

const MAX_BACKOFF_MS = 15_000;

function backoffFor(attempt: number): number {
  const base = Math.min(MAX_BACKOFF_MS, 400 * 2 ** attempt);
  // Jitter so a server restart doesn't bring every client back in lockstep.
  return base / 2 + Math.random() * (base / 2);
}

export interface GameSocket {
  game: GameDto | null;
  /**
   * Local `Date.now()` when `game` arrived. Clocks count down from this rather
   * than from a server timestamp, so a browser whose wall clock is wrong still
   * shows the right remaining time.
   */
  gameAt: number;
  online: string[];
  error: string | null;
  connected: boolean;
  propose: (move: ProposedMove) => void;
  ready: () => void;
  resign: () => void;
  offerDraw: () => void;
  respondToDraw: (accept: boolean) => void;
  clearError: () => void;
}

export function useGameSocket(gameId: string | null): GameSocket {
  const [game, setGameState] = useState<GameDto | null>(null);
  const [gameAt, setGameAt] = useState(() => Date.now());
  const [online, setOnline] = useState<string[]>([]);

  const setGame = useCallback((next: GameDto) => {
    setGameState(next);
    setGameAt(Date.now());
  }, []);
  const [error, setError] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  const socketRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    if (!gameId) return;

    setGameState(null);
    setError(null);

    let disposed = false;
    let attempt = 0;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    const connect = () => {
      const scheme = window.location.protocol === "https:" ? "wss:" : "ws:";
      const socket = new WebSocket(`${scheme}//${window.location.host}${WS_PATH}`);
      socketRef.current = socket;

      socket.onopen = () => {
        attempt = 0;
        setConnected(true);
        // The server answers a subscribe with a full snapshot, so this doubles
        // as the initial load and as recovery after any disconnect.
        socket.send(JSON.stringify({ t: "subscribe", gameId } satisfies ClientMessage));
      };

      socket.onmessage = (event) => {
        let message: ServerMessage;
        try {
          message = JSON.parse(String(event.data)) as ServerMessage;
        } catch {
          return;
        }

        switch (message.t) {
          case "state":
            setGame(message.game);
            break;
          case "moved":
            setGame(message.game);
            setError(null);
            break;
          case "presence":
            setOnline(message.online);
            break;
          case "error":
            setError(message.message);
            break;
          case "pong":
            break;
        }
      };

      socket.onclose = () => {
        setConnected(false);
        if (disposed) return;
        retryTimer = setTimeout(connect, backoffFor(attempt++));
      };

      socket.onerror = () => socket.close();
    };

    connect();
    // setGame is stable; re-running this effect would drop the connection.

    return () => {
      disposed = true;
      if (retryTimer) clearTimeout(retryTimer);
      socketRef.current?.close();
      socketRef.current = null;
    };
  }, [gameId, setGame]);

  const send = useCallback((message: ClientMessage) => {
    const socket = socketRef.current;
    if (socket?.readyState !== WebSocket.OPEN) {
      setError("Not connected — reconnecting…");
      return;
    }
    socket.send(JSON.stringify(message));
  }, []);

  const propose = useCallback(
    (move: ProposedMove) => {
      if (gameId) send({ t: "propose", gameId, move });
    },
    [gameId, send],
  );

  const ready = useCallback(() => {
    if (gameId) send({ t: "ready", gameId });
  }, [gameId, send]);

  const resign = useCallback(() => {
    if (gameId) send({ t: "resign", gameId });
  }, [gameId, send]);

  const offerDraw = useCallback(() => {
    if (gameId) send({ t: "offer_draw", gameId });
  }, [gameId, send]);

  const respondToDraw = useCallback(
    (accept: boolean) => {
      if (gameId) send({ t: "respond_draw", gameId, accept });
    },
    [gameId, send],
  );

  const clearError = useCallback(() => setError(null), []);

  return {
    game,
    gameAt,
    online,
    error,
    connected,
    propose,
    ready,
    resign,
    offerDraw,
    respondToDraw,
    clearError,
  };
}
